import React, { useState, useEffect, useMemo } from 'react';
import {
  Building2, DollarSign, FileText, Calendar, Clock,
  Search, TrendingUp, AlertCircle, RefreshCw, ChevronRight, Image as ImageIcon,
  Zap, Briefcase, ListTodo, Users, Crown, ReceiptText, Wrench, Sparkles,
  BarChart3, Globe, Compass, Target, Newspaper,
} from 'lucide-react';
import { APP_SECTIONS } from '@techstack/core';
import apiClient from '../../lib/apiClient';
import agencyImagesApi from '../../lib/agencyImagesApi';
import { useRole } from '../../hooks/useRole';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import AgencyImageLibrary from '../AgencyImageLibrary/AgencyImageLibrary';
import NewsStrip from '../NewsFeed/NewsStrip';
import '../shared/shared-chrome.css';
import './Home.css';

// GAS-shell parity (review doc §9, P2): the home page opens with the GAS
// HomeView anatomy — hero + one big search, a stat row, then jump tiles
// in the shared APP_SECTIONS order. Tiles map shared section keys to
// TekStak routes + gates; sections with no TekStak surface (or in-tenant
// only) simply don't map. TekStak-only extras follow the shared block.
const TILE_ROUTES = {
  execution: { id: 'command-center', icon: Zap, show: (r) => r.isAdmin },
  customers: { id: 'accounts', icon: Briefcase, show: (r) => r.isAdmin },
  tasks: { id: 'tasks', icon: ListTodo, show: (r) => r.isAdmin },
  contacts: { id: 'contacts', icon: Users, show: (r) => r.isAdmin },
  opportunities: { id: 'opportunities', icon: Target, show: (r) => r.isAdmin },
  vendors: { id: 'vendors', icon: Crown, show: (r) => r.isFeatureEnabled('vendor_analysis') },
  news: { id: 'news', icon: Newspaper, show: (r) => r.isFeatureEnabled('news_feed') },
  quotes: { id: 'quotes', icon: ReceiptText, show: (r) => r.isAdmin },
  tools: { id: 'classification-hub', icon: Wrench, show: (r) => r.isFeatureEnabled('classification_hub') },
};

const EXTRA_TILES = [
  { key: 'sales-ai', id: 'sales-ai', label: 'Sales AI', blurb: 'AI canvas & artifacts', icon: Sparkles, show: (r) => r.isAdmin },
  { key: 'analysis', id: 'tech-stack', label: 'Analysis', blurb: 'Tech stack, spending, classification', icon: BarChart3, show: (r) => r.isFeatureEnabled('tech_stack') },
  { key: 'stack-map', id: 'stack-map', label: 'Stack Map', blurb: '3D account landscape', icon: Globe, show: (r) => r.isFeatureEnabled('stack_map') },
  { key: 'atlas', id: 'atlas/home', label: 'Atlas', blurb: 'Security portfolio education', icon: Compass, show: (r) => r.isFeatureEnabled('atlas') },
];

// Agency logo with graceful fallback to the acronym badge (no layout shift — both
// occupy the same 44px slot; a broken/missing image reverts to the badge).
function CardLogo({ url, acronym }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => { setBroken(false); }, [url]);
  if (url && !broken) {
    return (
      <span className="home-card-logo">
        <img src={url} alt="" loading="lazy" onError={() => setBroken(true)} />
      </span>
    );
  }
  return <span className="home-card-acronym">{acronym || '?'}</span>;
}

const FRESHNESS_CONFIG = {
  current: { label: 'Current', color: '#10b981', bg: 'rgba(16, 185, 129, 0.1)' },
  aging: { label: 'Aging', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)' },
  stale: { label: 'Stale', color: '#f97316', bg: 'rgba(249, 115, 22, 0.1)' },
  very_stale: { label: 'Very Stale', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)' },
  no_data: { label: 'No Data', color: '#71717a', bg: 'rgba(113, 113, 122, 0.1)' },
};

export default function Home({ departments, onDepartmentSelect, onNavigate }) {
  const role = useRole();
  const [coverage, setCoverage] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('spend'); // spend, records, name, freshness
  const [hideNoData, setHideNoData] = useState(true); // Default to hiding no-data departments
  const [logoMap, setLogoMap] = useState({}); // department_id -> default logo url
  const [libraryDept, setLibraryDept] = useState(null); // agency whose Image Library is open
  useDocumentTitle('Home');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      // limit=500: the endpoint's default LIMIT 100 combined with its
      // gaps-first ORDER BY (no_data departments sort FIRST — it serves
      // the Data Gaps page) used to hand this grid the 100 emptiest
      // departments, which the with-data filter then reduced to zero.
      // Ask for the full set; this grid sorts client-side anyway.
      const [coverageData, statsData] = await Promise.all([
        apiClient.get('/api/gap-analysis/coverage', { query: { limit: 500 } }),
        apiClient.get('/api/gap-analysis/coverage/stats'),
      ]);
      setCoverage(coverageData || []);
      setStats(statsData || null);
    } catch (err) {
      setError(err.message || 'Failed to load department data');
    } finally {
      setLoading(false);
    }
  };

  // Merge coverage data with departments list for complete cards
  const departmentCards = useMemo(() => {
    const coverageMap = new Map();
    (coverage || []).forEach(c => coverageMap.set(c.department_id, c));

    // Use coverage data as primary (it has the stats), fall back to departments list
    const allDepts = coverage.length > 0
      ? coverage
      : (departments || []).map(d => ({
          department_id: d.department_id,
          official_name: d.official_name,
          common_name: d.common_name,
          acronym: d.acronym,
          total_records: 0,
          total_spend: 0,
          freshness_status: 'no_data',
        }));

    // Filter out no-data departments if toggle is on. "Has data" means
    // actual spend or records — NOT the freshness flag, which derives
    // from latest_data and can read no_data for departments that do
    // have rows (freshness stays a display-only chip on the card).
    let filtered = hideNoData
      ? allDepts.filter(d => parseFloat(d.total_spend) > 0 || Number(d.total_records) > 0)
      : allDepts;

    // Filter by search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(d =>
        (d.official_name || '').toLowerCase().includes(q) ||
        (d.common_name || '').toLowerCase().includes(q) ||
        (d.acronym || '').toLowerCase().includes(q)
      );
    }

    // Sort
    return [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'spend':
          return (parseFloat(b.total_spend) || 0) - (parseFloat(a.total_spend) || 0);
        case 'records':
          return (b.total_records || 0) - (a.total_records || 0);
        case 'name':
          return (a.common_name || a.official_name || '').localeCompare(b.common_name || b.official_name || '');
        case 'freshness': {
          const order = { current: 0, aging: 1, stale: 2, very_stale: 3, no_data: 4 };
          return (order[a.freshness_status] || 4) - (order[b.freshness_status] || 4);
        }
        default:
          return 0;
      }
    });
  }, [coverage, departments, searchQuery, sortBy, hideNoData]);

  // Resolve default logos for the visible cards (one bulk call, keyed by the id set
  // so it only refetches when the visible agencies actually change).
  const visibleIds = useMemo(
    () => departmentCards.map(d => d.department_id).filter(Boolean),
    [departmentCards]
  );
  const idsKey = visibleIds.join(',');

  const loadLogos = React.useCallback(async (ids) => {
    if (!ids.length) { setLogoMap({}); return; }
    try {
      const res = await agencyImagesApi.defaults(ids);
      setLogoMap(res?.defaults || {});
    } catch { /* logos are best-effort; cards fall back to the acronym badge */ }
  }, []);

  useEffect(() => {
    loadLogos(idsKey ? idsKey.split(',').map(Number) : []);
  }, [idsKey, loadLogos]);

  // Count of hidden no-data departments (mirror of the filter above)
  const noDataCount = useMemo(() => {
    return (coverage || []).filter(d => !(parseFloat(d.total_spend) > 0 || Number(d.total_records) > 0)).length;
  }, [coverage]);

  const handleCardClick = (dept) => {
    onDepartmentSelect?.(dept.department_id);
    onNavigate?.('tech-stack');
  };

  const formatSpend = (amount) => {
    const num = parseFloat(amount) || 0;
    if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(1)}M`;
    if (num >= 1e3) return `$${(num / 1e3).toFixed(0)}K`;
    return `$${num.toFixed(0)}`;
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    return parseInt(num).toLocaleString();
  };

  const formatDateRange = (earliest, latest) => {
    if (!earliest && !latest) return 'No data';
    const fmt = (d) => {
      if (!d) return '?';
      const date = new Date(d);
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    };
    return `${fmt(earliest)} – ${fmt(latest)}`;
  };

  if (loading) {
    return (
      <div className="home-page">
        <div className="home-loading">
          <RefreshCw size={24} className="home-spin" />
          <p>Loading departments...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="home-page">
        <div className="home-error">
          <AlertCircle size={32} />
          <p>{error}</p>
          <button onClick={fetchData} className="home-retry-btn">Retry</button>
        </div>
      </div>
    );
  }

  // Jump tiles: shared APP_SECTIONS order first, TekStak extras after.
  const jumpTiles = [
    ...APP_SECTIONS
      .filter((s) => !s.inTenantOnly && TILE_ROUTES[s.key])
      .map((s) => ({ key: s.key, label: s.label, blurb: s.blurb, ...TILE_ROUTES[s.key] }))
      .filter((t) => t.show(role)),
    ...EXTRA_TILES.filter((t) => t.show(role)),
  ];

  return (
    <div className="home-page">
      {/* GAS-parity hero (review doc §9, P2): one question, one big search */}
      <div className="home-hero">
        <h1>What do you need to find?</h1>
        <p>Search any California agency to jump into its live data, or use the menu.</p>
        <div className="home-search home-search--hero">
          <Search size={18} className="home-search-icon" />
          {/* aria-label, not just a placeholder: a placeholder disappears on
              the first keystroke and is not a reliable accessible name. */}
          <input
            type="text"
            aria-label="Search agencies"
            placeholder="Search any agency (try DMV, prison, tax)…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="home-search-input"
          />
        </div>
      </div>

      {/* Stat row — the shared GAS-parity pattern (shared-chrome.css, P4) */}
      {stats && (
        <div className="ts-stat-row home-stat-row">
          <div className="ts-stat-card">
            <div className="ts-stat-card__value">{stats.total_departments || 0}</div>
            <div className="ts-stat-card__label">Agencies</div>
          </div>
          <div className="ts-stat-card">
            <div className="ts-stat-card__value">{formatSpend(stats.total_spend)}</div>
            <div className="ts-stat-card__label">Total spend</div>
          </div>
          <div className="ts-stat-card">
            <div className="ts-stat-card__value">{formatNumber(stats.total_records)}</div>
            <div className="ts-stat-card__label">Procurement records</div>
          </div>
        </div>
      )}

      {/* Front page: the freshest news at the caller's accounts (or statewide) */}
      {role.isFeatureEnabled('news_feed') && <NewsStrip limit={6} />}

      {/* Jump tiles — shared section registry order (@techstack/core) */}
      {jumpTiles.length > 0 && (
        <div className="home-jumps">
          {jumpTiles.map((t) => {
            const TileIcon = t.icon;
            return (
              <button key={t.key} type="button" className="ts-card ts-card--hover home-jump" onClick={() => onNavigate?.(t.id)}>
                <TileIcon size={18} className="home-jump-icon" />
                <div className="home-jump-title">{t.label}</div>
                <div className="home-jump-blurb">{t.blurb}</div>
              </button>
            );
          })}
        </div>
      )}

      {/* Agency coverage — the TekStak-only data grid, below the fold */}
      <div className="home-controls">
        <h2 className="home-coverage-title">Agency coverage</h2>
        <div className="home-filters">
          <button
            className={`home-filter-toggle ${hideNoData ? 'active' : ''}`}
            onClick={() => setHideNoData(!hideNoData)}
            title={hideNoData ? 'Show all departments' : 'Hide departments with no data'}
          >
            {hideNoData ? `Showing ${departmentCards.length} with data` : 'Show all'}
            {hideNoData && noDataCount > 0 && (
              <span className="home-hidden-count">+{noDataCount} hidden</span>
            )}
          </button>
        </div>
        <div className="home-sort">
          <span className="home-sort-label">Sort by:</span>
          {[
            { key: 'spend', label: 'Spend' },
            { key: 'records', label: 'Records' },
            { key: 'name', label: 'Name' },
            { key: 'freshness', label: 'Freshness' },
          ].map(opt => (
            <button
              key={opt.key}
              className={`home-sort-btn ${sortBy === opt.key ? 'active' : ''}`}
              onClick={() => setSortBy(opt.key)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Department Cards Grid */}
      <div className="home-grid">
        {departmentCards.map(dept => {
          const freshness = FRESHNESS_CONFIG[dept.freshness_status] || FRESHNESS_CONFIG.no_data;
          const spend = parseFloat(dept.total_spend) || 0;

          return (
            <button
              key={dept.department_id}
              className="home-card"
              onClick={() => handleCardClick(dept)}
            >
              <div className="home-card-header">
                <div className="home-card-name">
                  <CardLogo url={logoMap[dept.department_id]} acronym={dept.acronym} />
                  <div>
                    <h3>{dept.common_name || dept.official_name}</h3>
                    {dept.common_name && dept.official_name && dept.common_name !== dept.official_name && (
                      <p className="home-card-official">{dept.official_name}</p>
                    )}
                  </div>
                </div>
                <span className="home-card-header-actions">
                  <span
                    className="home-card-imgbtn"
                    role="button"
                    tabIndex={0}
                    title="Image library"
                    aria-label={`Open image library for ${dept.common_name || dept.official_name}`}
                    onClick={(e) => { e.stopPropagation(); setLibraryDept(dept); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); e.preventDefault(); setLibraryDept(dept); } }}
                  >
                    <ImageIcon size={14} />
                  </span>
                  <ChevronRight size={16} className="home-card-chevron" />
                </span>
              </div>

              <div className="home-card-stats">
                <div className="home-card-stat">
                  <DollarSign size={14} />
                  <span className="home-card-stat-value">{formatSpend(spend)}</span>
                  <span className="home-card-stat-label">Spend</span>
                </div>
                <div className="home-card-stat">
                  <FileText size={14} />
                  <span className="home-card-stat-value">{formatNumber(dept.total_records)}</span>
                  <span className="home-card-stat-label">Records</span>
                </div>
                <div className="home-card-stat">
                  <Calendar size={14} />
                  <span className="home-card-stat-value">{dept.months_with_data || 0}</span>
                  <span className="home-card-stat-label">Months</span>
                </div>
              </div>

              <div className="home-card-footer">
                <span className="home-card-date-range">
                  <Clock size={12} />
                  {formatDateRange(dept.earliest_data, dept.latest_data)}
                </span>
                <span
                  className="home-card-freshness"
                  style={{ color: freshness.color, background: freshness.bg }}
                >
                  {freshness.label}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {departmentCards.length === 0 && (
        <div className="home-empty">
          <Building2 size={32} />
          <p>{searchQuery ? 'No departments match your search.' : 'No department data available.'}</p>
        </div>
      )}

      {libraryDept && (
        <AgencyImageLibrary
          agencyId={libraryDept.department_id}
          agencyName={libraryDept.common_name || libraryDept.official_name}
          onClose={() => setLibraryDept(null)}
          onDefaultChanged={() => loadLogos(visibleIds)}
        />
      )}
    </div>
  );
}
